import * as React from 'react';
import {
    useErrorToastContext,
    useInfoToastContext,
    useInProgressToastContext,
    useSuccessToastContext,
    useWarningToastContext,
} from '../components/toast/hooks/toast-hooks';

// Toast intent constants
const ToastIntent = {
    Info: 'info',
    Success: 'success',
    Warning: 'warning',
    Error: 'error',
    InProgress: 'inProgress',
} as const;

export interface ShowToastOptions {
    intent: 'info' | 'success' | 'warning' | 'error' | 'inProgress';
    title?: React.ReactNode;
    children: React.ReactNode;
    autoHideDelay?: number;
    devBoxName?: string;
}

export interface ToastOptions {
    title?: React.ReactNode;
    autoHideDelay?: number;
    devBoxName?: string;
}

export const useToast = (): {
    show: (options: ShowToastOptions) => string;
    showSuccessToast: (
        message: React.ReactNode,
        title?: React.ReactNode,
        autoHideDelay?: number,
        devBoxName?: string
    ) => string;
    showErrorToast: (
        message: React.ReactNode,
        title?: React.ReactNode,
        autoHideDelay?: number,
        devBoxName?: string
    ) => string;
    showWarningToast: (
        message: React.ReactNode,
        title?: React.ReactNode,
        autoHideDelay?: number,
        devBoxName?: string
    ) => string;
    showInfoToast: (
        message: React.ReactNode,
        title?: React.ReactNode,
        autoHideDelay?: number,
        devBoxName?: string
    ) => string;
    showInProgressToast: (
        message: React.ReactNode,
        title?: React.ReactNode,
        autoHideDelay?: number,
        devBoxName?: string
    ) => string;
    showSuccessToastWithOptions: (message: React.ReactNode, options?: ToastOptions) => string;
    showErrorToastWithOptions: (message: React.ReactNode, options?: ToastOptions) => string;
    showWarningToastWithOptions: (message: React.ReactNode, options?: ToastOptions) => string;
    showInfoToastWithOptions: (message: React.ReactNode, options?: ToastOptions) => string;
    showInProgressToastWithOptions: (message: React.ReactNode, options?: ToastOptions) => string;
    hideToast: (id: string) => void;
    clearToast: () => void;
} => {
    const { openSurface: openInfo, closeSurface: closeInfo } = useInfoToastContext();
    const { openSurface: openSuccess, closeSurface: closeSuccess } = useSuccessToastContext();
    const { openSurface: openError, closeSurface: closeError } = useErrorToastContext();
    const { openSurface: openWarning, closeSurface: closeWarning } = useWarningToastContext();
    const { openSurface: openInProgress, closeSurface: closeInProgress } = useInProgressToastContext();

    // Debouncing state to prevent rapid successive calls
    const debounceTimers = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

    const debouncedCall = React.useCallback((key: string, fn: () => void, delay = 50) => {
        // Clear existing timer for this key
        const existingTimer = debounceTimers.current.get(key);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        // Set new timer
        const timer = setTimeout(() => {
            fn();
            debounceTimers.current.delete(key);
        }, delay);

        debounceTimers.current.set(key, timer);
    }, []);

    // Cleanup timers on unmount
    React.useEffect(() => {
        return () => {
            debounceTimers.current.forEach((timer) => clearTimeout(timer));
            debounceTimers.current.clear();
        };
    }, []);

    const show = React.useCallback(
        (options: ShowToastOptions): string => {
            const properties = {
                title: options.title,
                children: options.children,
                autoHideDelay: options.autoHideDelay,
                devBoxName: options.devBoxName,
            };

            // Close all other toasts first to ensure only one toast is visible at a time
            switch (options.intent) {
                case ToastIntent.Info:
                    closeSuccess();
                    closeError();
                    closeWarning();
                    closeInProgress();
                    openInfo(properties);
                    break;
                case ToastIntent.Success:
                    closeInfo();
                    closeError();
                    closeWarning();
                    closeInProgress();
                    openSuccess(properties);
                    break;
                case ToastIntent.Error:
                    closeInfo();
                    closeSuccess();
                    closeWarning();
                    closeInProgress();
                    openError(properties);
                    break;
                case ToastIntent.Warning:
                    closeInfo();
                    closeSuccess();
                    closeError();
                    closeInProgress();
                    openWarning(properties);
                    break;
                case ToastIntent.InProgress:
                    closeInfo();
                    closeSuccess();
                    closeError();
                    closeWarning();
                    openInProgress(properties);
                    break;
                default:
                    closeSuccess();
                    closeError();
                    closeWarning();
                    closeInProgress();
                    openInfo(properties);
                    break;
            }

            // Return a unique ID for consistency
            return `toast-${Date.now()}-${Math.random()}`;
        },
        [
            openInfo,
            openSuccess,
            openError,
            openWarning,
            openInProgress,
            closeInfo,
            closeSuccess,
            closeError,
            closeWarning,
            closeInProgress,
        ]
    );

    // Methods for backward compatibility with debouncing
    const showSuccessToast = React.useCallback(
        (message: React.ReactNode, title?: React.ReactNode, autoHideDelay = 3000, devBoxName?: string) => {
            const key = `${ToastIntent.Success}-${devBoxName || 'default'}`;
            debouncedCall(key, () => {
                // Close all other toasts to ensure only one toast is visible at a time
                closeInfo();
                closeError();
                closeWarning();
                closeInProgress();
                openSuccess({
                    title,
                    children: message,
                    autoHideDelay,
                    devBoxName,
                });
            });
            return `${ToastIntent.Success}-toast-${Date.now()}`;
        },
        [openSuccess, closeInfo, closeError, closeWarning, closeInProgress, debouncedCall]
    );

    const showErrorToast = React.useCallback(
        (message: React.ReactNode, title?: React.ReactNode, autoHideDelay = 5000, devBoxName?: string) => {
            const key = `${ToastIntent.Error}-${devBoxName || 'default'}`;
            debouncedCall(key, () => {
                // Close all other toasts to ensure only one toast is visible at a time
                closeInfo();
                closeSuccess();
                closeWarning();
                closeInProgress();
                openError({
                    title,
                    children: message,
                    autoHideDelay,
                    devBoxName,
                });
            });
            return `${ToastIntent.Error}-toast-${Date.now()}`;
        },
        [openError, closeInfo, closeSuccess, closeWarning, closeInProgress, debouncedCall]
    );

    const showWarningToast = React.useCallback(
        (message: React.ReactNode, title?: React.ReactNode, autoHideDelay = 4000, devBoxName?: string) => {
            const key = `${ToastIntent.Warning}-${devBoxName || 'default'}`;
            debouncedCall(key, () => {
                // Close all other toasts to ensure only one toast is visible at a time
                closeInfo();
                closeSuccess();
                closeError();
                closeInProgress();
                openWarning({
                    title,
                    children: message,
                    autoHideDelay,
                    devBoxName,
                });
            });
            return `${ToastIntent.Warning}-toast-${Date.now()}`;
        },
        [openWarning, closeInfo, closeSuccess, closeError, closeInProgress, debouncedCall]
    );

    const showInfoToast = React.useCallback(
        (message: React.ReactNode, title?: React.ReactNode, autoHideDelay = 3000, devBoxName?: string) => {
            const key = `${ToastIntent.Info}-${devBoxName || 'default'}`;
            debouncedCall(key, () => {
                // Close all other toasts to ensure only one toast is visible at a time
                closeSuccess();
                closeError();
                closeWarning();
                closeInProgress();
                openInfo({
                    title,
                    children: message,
                    autoHideDelay,
                    devBoxName,
                });
            });
            return `${ToastIntent.Info}-toast-${Date.now()}`;
        },
        [openInfo, closeSuccess, closeError, closeWarning, closeInProgress, debouncedCall]
    );

    const showInProgressToast = React.useCallback(
        (message: React.ReactNode, title?: React.ReactNode, autoHideDelay = 0, devBoxName?: string) => {
            const key = `${ToastIntent.InProgress}-${devBoxName || 'default'}`;
            debouncedCall(key, () => {
                // Close all other toasts to ensure only one toast is visible at a time
                closeInfo();
                closeSuccess();
                closeError();
                closeWarning();
                openInProgress({
                    title,
                    children: message,
                    autoHideDelay, // Usually 0 for progress toasts (no auto-hide)
                    devBoxName,
                });
            });
            return `${ToastIntent.InProgress}-toast-${Date.now()}`;
        },
        [openInProgress, closeInfo, closeSuccess, closeError, closeWarning, debouncedCall]
    );

    const showSuccessToastWithOptions = React.useCallback(
        (message: React.ReactNode, options?: ToastOptions) => {
            // Close all other toasts to ensure only one toast is visible at a time
            closeInfo();
            closeError();
            closeWarning();
            closeInProgress();
            openSuccess({
                title: options?.title,
                children: message,
                autoHideDelay: options?.autoHideDelay ?? 3000,
                devBoxName: options?.devBoxName,
            });
            return `${ToastIntent.Success}-toast-${Date.now()}`;
        },
        [openSuccess, closeInfo, closeError, closeWarning, closeInProgress]
    );

    const showErrorToastWithOptions = React.useCallback(
        (message: React.ReactNode, options?: ToastOptions) => {
            // Close all other toasts to ensure only one toast is visible at a time
            closeInfo();
            closeSuccess();
            closeWarning();
            closeInProgress();
            openError({
                title: options?.title,
                children: message,
                autoHideDelay: options?.autoHideDelay ?? 5000,
                devBoxName: options?.devBoxName,
            });
            return `${ToastIntent.Error}-toast-${Date.now()}`;
        },
        [openError, closeInfo, closeSuccess, closeWarning, closeInProgress]
    );

    const showWarningToastWithOptions = React.useCallback(
        (message: React.ReactNode, options?: ToastOptions) => {
            // Close all other toasts to ensure only one toast is visible at a time
            closeInfo();
            closeSuccess();
            closeError();
            closeInProgress();
            openWarning({
                title: options?.title,
                children: message,
                autoHideDelay: options?.autoHideDelay ?? 4000,
                devBoxName: options?.devBoxName,
            });
            return `${ToastIntent.Warning}-toast-${Date.now()}`;
        },
        [openWarning, closeInfo, closeSuccess, closeError, closeInProgress]
    );

    const showInfoToastWithOptions = React.useCallback(
        (message: React.ReactNode, options?: ToastOptions) => {
            // Close all other toasts to ensure only one toast is visible at a time
            closeSuccess();
            closeError();
            closeWarning();
            closeInProgress();
            openInfo({
                title: options?.title,
                children: message,
                autoHideDelay: options?.autoHideDelay ?? 3000,
                devBoxName: options?.devBoxName,
            });
            return `${ToastIntent.Info}-toast-${Date.now()}`;
        },
        [openInfo, closeSuccess, closeError, closeWarning, closeInProgress]
    );

    const showInProgressToastWithOptions = React.useCallback(
        (message: React.ReactNode, options?: ToastOptions) => {
            // Close all other toasts to ensure only one toast is visible at a time
            closeInfo();
            closeSuccess();
            closeError();
            closeWarning();
            openInProgress({
                title: options?.title,
                children: message,
                autoHideDelay: options?.autoHideDelay ?? 0, // Default to no auto-hide for progress toasts
                devBoxName: options?.devBoxName,
            });
            return `${ToastIntent.InProgress}-toast-${Date.now()}`;
        },
        [openInProgress, closeInfo, closeSuccess, closeError, closeWarning]
    );

    const hideToast = React.useCallback(
        (id: string) => {
            // Parse the toast type from the ID and close the appropriate one
            if (id.includes(ToastIntent.Info)) {
                closeInfo();
            } else if (id.includes(ToastIntent.Success)) {
                closeSuccess();
            } else if (id.includes(ToastIntent.Error)) {
                closeError();
            } else if (id.includes(ToastIntent.Warning)) {
                closeWarning();
            } else if (id.includes(ToastIntent.InProgress)) {
                closeInProgress();
            } else {
                // If ID format is unknown, close all to be safe
                // Only the relevant one will actually close
                closeInfo();
                closeSuccess();
                closeError();
                closeWarning();
                closeInProgress();
            }
        },
        [closeInfo, closeSuccess, closeError, closeWarning, closeInProgress]
    );

    const clearToast = React.useCallback(() => {
        closeInfo();
        closeSuccess();
        closeError();
        closeWarning();
        closeInProgress();
    }, [closeInfo, closeSuccess, closeError, closeWarning, closeInProgress]);

    return {
        show,
        showSuccessToast,
        showErrorToast,
        showWarningToast,
        showInfoToast,
        showInProgressToast,
        showSuccessToastWithOptions,
        showErrorToastWithOptions,
        showWarningToastWithOptions,
        showInfoToastWithOptions,
        showInProgressToastWithOptions,
        hideToast,
        clearToast,
    };
};
